/** Browser-only, origin-scoped analytics preference. No identifier is generated. */
export const ANALYTICS_CONSENT_KEY = "amoreg.analytics-consent.v1";
export const ANALYTICS_CONSENT_EVENT = "amoreg:analytics-consent";
export const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;
export type AnalyticsConsent = "unknown" | "accepted" | "rejected";

export function parseAnalyticsConsent(raw: string | null, now = Date.now()): AnalyticsConsent {
  if (!raw || raw.length > 1024) return "unknown";
  try {
    const record = JSON.parse(raw);
    if (record?.version !== 1 || record.scope !== "analytics" || typeof record.analytics !== "boolean") return "unknown";
    const timestamp = Date.parse(record.updatedAt);
    if (!Number.isFinite(timestamp) || timestamp > now || now - timestamp >= CONSENT_MAX_AGE_MS) return "unknown";
    return record.analytics ? "accepted" : "rejected";
  } catch {
    return "unknown";
  }
}

export function analyticsConsentRecord(analytics: boolean, now = Date.now()): string {
  return JSON.stringify({ version: 1, scope: "analytics", analytics, updatedAt: new Date(now).toISOString() });
}

let sessionPreference: string | null = null;

export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === "undefined") return "unknown";
  if (sessionPreference !== null) return parseAnalyticsConsent(sessionPreference);
  try {
    return parseAnalyticsConsent(window.localStorage.getItem(ANALYTICS_CONSENT_KEY));
  } catch {
    return parseAnalyticsConsent(sessionPreference);
  }
}

export function setAnalyticsConsent(analytics: boolean): void {
  sessionPreference = analyticsConsentRecord(analytics);
  try { window.localStorage.setItem(ANALYTICS_CONSENT_KEY, sessionPreference); } catch { /* Session-only choice. */ }
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
}

/** Retire identifiers from earlier deployments without initializing a tracking SDK. */
export function clearPostHogStorage(projectKey: string | undefined): void {
  if (!projectKey || typeof window === "undefined") return;
  // Keep the SDK's essential opt-out marker; deleting it can undo withdrawal.
  const names = [`ph_${projectKey}_posthog`];
  // The SDK also keeps tab/session identifiers outside its primary record.
  for (const storageName of ["localStorage", "sessionStorage"] as const) {
    try {
      const storage = window[storageName];
      for (const name of Object.keys(storage)) {
        if (name.startsWith(`ph_${projectKey}_`)) storage.removeItem(name);
      }
    } catch { /* Storage may be blocked. */ }
  }
  for (const name of names) {
    for (const domain of ["", window.location.hostname, ...(window.location.hostname.endsWith(".amoreg.com") ? ["amoreg.com"] : [])]) {
      document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
    }
  }
}

export function subscribeAnalyticsConsent(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const storage = (event: StorageEvent) => {
    if (event.key === null || event.key === ANALYTICS_CONSENT_KEY) {
      sessionPreference = null;
      onChange();
    }
  };
  window.addEventListener(ANALYTICS_CONSENT_EVENT, onChange);
  window.addEventListener("storage", storage);
  // Expiry also takes effect on an already-open tab.
  const expiry = window.setInterval(onChange, 60_000);
  return () => {
    window.removeEventListener(ANALYTICS_CONSENT_EVENT, onChange);
    window.removeEventListener("storage", storage);
    window.clearInterval(expiry);
  };
}
